import React, { useEffect, useMemo, useState } from "react";
import {
  Users, Search, History, FileText, Mail, StickyNote, Edit, Plus, Trash2,
  Car, Calendar, Phone, User, CreditCard, GitMerge, AlertTriangle, MessageCircle,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { StatCard, SectionCard, EmptyState, StatusBadge, APPOINTMENT_STATUS, SearchInput } from "./AdminUI";
import DuplicatesModal, { clientHasPotentialDuplicate } from "./DuplicatesModal";
import PhoneDuplicateNudge from "./PhoneDuplicateNudge";
import { sanitizePhoneInput, normalizePhone, phoneError, toWhatsAppNumber } from "../utils/phone";

const ClientsTab = ({ ctx }) => {
  const {
    clients, clientSearch, handleSearchClients,
    selectedClientHistory, setSelectedClientHistory, fetchClientHistory, clientHistory,
    isEditingClient, setIsEditingClient, editClientForm, setEditClientForm,
    startEditingClient, handleUpdateClient, handleUpdateClientPlate,
    newNote, setNewNote, handleAddNote, handleDeleteNote,
    // Standalone client CRUD
    creatingClient, setCreatingClient,
    newClientForm, setNewClientForm,
    handleCreateClient, handleDeleteClientCard,
  } = ctx;

  // Normalisation helpers used on text inputs so saved data is always clean.
  // Names: capitalize the first letter of each word (handles compound names like Jean-Pierre).
  const capitalizeName = (raw) =>
    (raw || "")
      .split(/(\s+|-)/)
      .map((p) => (!p || /^(\s+|-)$/.test(p) ? p : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()))
      .join("");
  const upperPlate = (raw) => (raw || "").toUpperCase();

  const activeClient = clients.find((c) => c.client_email === selectedClientHistory);

  // Post-save nudge: the edited phone matches another client → propose merge.
  const [phoneNudge, setPhoneNudge] = useState(null);
  useEffect(() => {
    if (phoneNudge && phoneNudge.email !== selectedClientHistory) setPhoneNudge(null);
  }, [selectedClientHistory, phoneNudge]);

  const submitEditClient = async (e) => {
    const warnings = await handleUpdateClient(e);
    if (warnings?.length) {
      setPhoneNudge({
        email: (editClientForm.email || selectedClientHistory).trim().toLowerCase(),
        phone: normalizePhone(editClientForm.phone),
        candidates: warnings,
      });
    }
  };

  // Duplicate-detection state + per-client suspicion set (so we can show a
  // "Doublon ?" badge directly on each row without hitting the server for
  // every item).
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const suspectedEmails = useMemo(() => {
    const set = new Set();
    for (const c of clients) {
      if (clientHasPotentialDuplicate(c, clients)) set.add(c.client_email);
    }
    return set;
  }, [clients]);
  const duplicateCount = suspectedEmails.size;

  const [focusPhone, setFocusPhone] = useState(null);

  const handleFetchClientsRefresh = () => {
    // Reload the list after a merge.
    setPhoneNudge(null);
    ctx.fetchClients?.();
    if (selectedClientHistory) ctx.fetchClientHistory?.(selectedClientHistory);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Users size={20} className="text-[#FF3B30]" />
          Clients
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-white/50 text-xs">{clients.length} client(s)</span>
          <button
            onClick={() => { setFocusPhone(null); setDuplicatesOpen(true); }}
            data-testid="client-duplicates-btn"
            className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded border transition-colors ${
              duplicateCount > 0
                ? "bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border-amber-500/30"
                : "bg-white/5 hover:bg-white/10 text-white/70 border-white/[0.08]"
            }`}
          >
            <GitMerge size={14} /> Doublons
            {duplicateCount > 0 && (
              <span className="ml-1 bg-amber-500/30 text-amber-100 rounded-full px-1.5 py-[1px] text-[10px]">
                {duplicateCount}
              </span>
            )}
          </button>
          <button
            onClick={() => { setCreatingClient(true); setNewClientForm({ name: "", email: "", phone: "", plates: "" }); }}
            data-testid="client-create-btn"
            className="inline-flex items-center gap-1.5 bg-[#FF3B30] hover:bg-[#D63025] text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded"
          >
            <Plus size={14} /> Nouveau client
          </button>
        </div>
      </div>

      {/* Create new client form */}
      {creatingClient && (
        <SectionCard title="Nouveau client" icon={Plus} iconColor="text-[#FF3B30]">
          <form onSubmit={handleCreateClient} className="grid md:grid-cols-2 gap-3" data-testid="client-create-form">
            <div>
              <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Nom complet</label>
              <Input
                value={newClientForm.name}
                onChange={(e) => setNewClientForm({ ...newClientForm, name: capitalizeName(e.target.value) })}
                placeholder="Jean Dupont"
                data-testid="client-create-name"
                autoCapitalize="words"
                autoComplete="name"
                required
                className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"
              />
            </div>
            <div>
              <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Email</label>
              <Input
                type="email"
                value={newClientForm.email}
                onChange={(e) => setNewClientForm({ ...newClientForm, email: e.target.value })}
                placeholder="jean@exemple.fr"
                data-testid="client-create-email"
                autoCapitalize="none"
                autoComplete="email"
                required
                className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"
              />
            </div>
            <div>
              <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Téléphone</label>
              <Input
                type="tel"
                inputMode="tel"
                value={newClientForm.phone}
                onChange={(e) => setNewClientForm({ ...newClientForm, phone: sanitizePhoneInput(e.target.value) })}
                placeholder="06 12 34 56 78"
                data-testid="client-create-phone"
                autoComplete="tel"
                aria-invalid={phoneError(newClientForm.phone) ? "true" : "false"}
                className={`bg-[#1C1C1E] text-white h-9 text-sm ${
                  phoneError(newClientForm.phone)
                    ? "border-red-500/60 focus-visible:ring-red-500/40"
                    : "border-white/[0.06]"
                }`}
              />
              {phoneError(newClientForm.phone) && (
                <p data-testid="client-create-phone-error" className="text-red-400 text-[11px] mt-1">
                  {phoneError(newClientForm.phone)}
                </p>
              )}
            </div>
            <div>
              <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Plaques (séparées par virgule)</label>
              <Input
                value={newClientForm.plates}
                onChange={(e) => setNewClientForm({ ...newClientForm, plates: upperPlate(e.target.value) })}
                placeholder="AB-123-CD, EF-456-GH"
                data-testid="client-create-plates"
                style={{ textTransform: "uppercase" }}
                autoCapitalize="characters"
                className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm font-mono"
              />
            </div>
            <div className="md:col-span-2 flex gap-2 pt-1">
              <button
                type="submit"
                disabled={!!phoneError(newClientForm.phone)}
                data-testid="client-create-save"
                className="flex-1 bg-[#FF3B30] hover:bg-[#D63025] disabled:bg-white/10 disabled:cursor-not-allowed text-white py-2 rounded-md text-sm font-semibold transition-colors"
              >
                Créer
              </button>
              <button
                type="button"
                onClick={() => setCreatingClient(false)}
                className="px-4 bg-white/[0.06] hover:bg-white/[0.1] text-white py-2 rounded-md text-sm transition-colors"
              >
                Annuler
              </button>
            </div>
          </form>
        </SectionCard>
      )}

      {/* Search */}
      <SectionCard title={null}>
        <SearchInput
          value={clientSearch}
          onChange={(e) => handleSearchClients(e.target.value)}
          placeholder="Rechercher par nom, email ou plaque..."
          icon={Search}
        />
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* List */}
        <div className="lg:col-span-1 space-y-2 max-h-[700px] overflow-y-auto pr-1">
          {clients.length === 0 ? (
            <EmptyState icon={Users} title="Aucun client trouvé" />
          ) : (
            clients.map((client) => {
              const active = selectedClientHistory === client.client_email;
              const isSuspected = suspectedEmails.has(client.client_email);
              return (
                <button
                  key={client.client_email}
                  onClick={() => { setSelectedClientHistory(client.client_email); fetchClientHistory(client.client_email); }}
                  data-testid={`client-row-${client.client_email}`}
                  className={`w-full text-left bg-[#141414] border rounded-xl p-3.5 cursor-pointer transition-all ${
                    active ? "border-[#FF3B30] shadow-md shadow-red-500/10" : "border-white/[0.06] hover:border-white/[0.12]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-white text-sm truncate">{client.client_name || "Sans nom"}</p>
                      <p className="text-white/50 text-xs truncate mt-0.5">{client.client_email}</p>
                    </div>
                    {isSuspected && (
                      <span
                        data-testid={`client-duplicate-badge-${client.client_email}`}
                        title="Doublon potentiel détecté"
                        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/30 px-1.5 py-0.5 rounded flex-shrink-0"
                      >
                        <AlertTriangle size={9} /> Doublon ?
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2 gap-2">
                    <span className="text-[11px] text-[#FF3B30] font-mono truncate">
                      {client.plates?.join(", ") || "—"}
                    </span>
                    <span className="text-[10px] text-white/40 bg-white/[0.04] px-1.5 py-0.5 rounded shrink-0">
                      {client.total_appointments} RDV
                    </span>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {!selectedClientHistory ? (
            <EmptyState icon={Users} title="Sélectionnez un client" description="Cliquez sur un client dans la liste pour voir son historique complet." />
          ) : (
            <div className="space-y-4">
              {phoneNudge && phoneNudge.email === selectedClientHistory && (
                <PhoneDuplicateNudge
                  candidates={phoneNudge.candidates}
                  onMerge={() => { setFocusPhone(phoneNudge.phone); setDuplicatesOpen(true); }}
                  onDismiss={() => setPhoneNudge(null)}
                />
              )}
              {/* Client Info */}
              <SectionCard
                title="Informations client"
                icon={User}
                iconColor="text-purple-400"
                action={!isEditingClient && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => startEditingClient(selectedClientHistory)}
                      data-testid="client-edit-btn"
                      className="bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border border-purple-500/20 px-3 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5"
                    >
                      <Edit size={12} /> Modifier
                    </button>
                    <button
                      onClick={() => handleDeleteClientCard(selectedClientHistory)}
                      data-testid="client-delete-btn"
                      title="Supprimer la fiche"
                      className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 px-2 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              >
                {isEditingClient ? (
                  <form onSubmit={submitEditClient} className="space-y-3" data-testid="client-edit-form">
                    <div>
                      <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Nom</label>
                      <Input
                        value={editClientForm.name}
                        onChange={(e) => setEditClientForm({ ...editClientForm, name: capitalizeName(e.target.value) })}
                        data-testid="client-edit-name"
                        autoCapitalize="words"
                        className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Email</label>
                      <Input
                        type="email"
                        value={editClientForm.email}
                        onChange={(e) => setEditClientForm({ ...editClientForm, email: e.target.value })}
                        data-testid="client-edit-email"
                        autoCapitalize="none"
                        className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Téléphone</label>
                      <Input
                        type="tel"
                        inputMode="tel"
                        value={editClientForm.phone}
                        onChange={(e) => setEditClientForm({ ...editClientForm, phone: sanitizePhoneInput(e.target.value) })}
                        data-testid="client-edit-phone"
                        aria-invalid={phoneError(editClientForm.phone) ? "true" : "false"}
                        className={`bg-[#1C1C1E] text-white h-9 text-sm ${
                          phoneError(editClientForm.phone)
                            ? "border-red-500/60 focus-visible:ring-red-500/40"
                            : "border-white/[0.06]"
                        }`}
                      />
                      {phoneError(editClientForm.phone) && (
                        <p data-testid="client-edit-phone-error" className="text-red-400 text-[11px] mt-1">
                          {phoneError(editClientForm.phone)}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Plaques (séparées par virgule)</label>
                      <Input
                        value={editClientForm.plates}
                        onChange={(e) => setEditClientForm({ ...editClientForm, plates: upperPlate(e.target.value) })}
                        data-testid="client-edit-plates"
                        style={{ textTransform: "uppercase" }}
                        autoCapitalize="characters"
                        placeholder="AB-123-CD, EF-456-GH"
                        className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm font-mono"
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        type="submit"
                        disabled={!!phoneError(editClientForm.phone)}
                        data-testid="client-edit-save"
                        className="flex-1 bg-purple-500 hover:bg-purple-600 disabled:bg-white/10 disabled:cursor-not-allowed text-white py-2 rounded-md text-sm font-semibold transition-colors"
                      >
                        Enregistrer
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsEditingClient(false)}
                        className="px-4 bg-white/[0.06] hover:bg-white/[0.1] text-white py-2 rounded-md text-sm transition-colors"
                      >
                        Annuler
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-white/40 text-[11px] uppercase tracking-wider flex items-center gap-1"><User size={10} /> Nom</p>
                      <p className="text-white mt-0.5">{activeClient?.client_name || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-[11px] uppercase tracking-wider flex items-center gap-1"><Mail size={10} /> Email</p>
                      <p className="text-white mt-0.5 truncate">{selectedClientHistory}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-[11px] uppercase tracking-wider flex items-center gap-1"><Phone size={10} /> Téléphone</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <p className="text-white">{activeClient?.client_phone || "N/A"}</p>
                        {activeClient?.client_phone && !phoneError(activeClient.client_phone) && (
                          <a
                            href={`https://wa.me/${toWhatsAppNumber(activeClient.client_phone)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="client-whatsapp-btn"
                            title="Ouvrir une conversation WhatsApp"
                            className="inline-flex items-center gap-1 bg-[#25D366]/15 hover:bg-[#25D366]/25 text-[#25D366] border border-[#25D366]/30 px-2 py-0.5 rounded-md text-[11px] font-semibold transition-colors"
                          >
                            <MessageCircle size={11} /> WhatsApp
                          </a>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-white/40 text-[11px] uppercase tracking-wider flex items-center gap-1"><Car size={10} /> Plaques</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[#FF3B30] font-mono text-sm font-bold">{activeClient?.plates?.join(", ") || "N/A"}</span>
                        <button
                          onClick={() => {
                            const p = prompt("Nouvelle plaque (sera convertie en majuscules) :", activeClient?.plates?.[0] || "");
                            if (p !== null) handleUpdateClientPlate(selectedClientHistory, (p || "").toUpperCase());
                          }}
                          data-testid="client-inline-edit-plate"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Edit size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </SectionCard>

              {/* Appointments */}
              <SectionCard title={`Rendez-vous (${clientHistory.appointments?.length || 0})`} icon={History} iconColor="text-cyan-400">
                <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1">
                  {clientHistory.appointments?.length === 0 ? (
                    <p className="text-white/40 text-center py-4 text-sm">Aucun rendez-vous</p>
                  ) : (
                    clientHistory.appointments?.map((apt) => {
                      const cfg = APPOINTMENT_STATUS[apt.status] || APPOINTMENT_STATUS.pending;
                      return (
                        <div key={apt.id} className="flex items-center gap-3 p-2.5 bg-[#1C1C1E] rounded-md border border-white/[0.04]">
                          <div className="bg-white/[0.06] px-2.5 py-1 rounded text-xs text-white/80 min-w-[85px] text-center">
                            <Calendar size={10} className="inline mr-1" />{apt.date}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{apt.service_name}</p>
                            <p className="text-[11px] text-white/40">{apt.time} • <span className="text-[#FF3B30]">{apt.client_plate}</span></p>
                          </div>
                          <StatusBadge config={cfg} />
                        </div>
                      );
                    })
                  )}
                </div>
              </SectionCard>

              {/* Quotes */}
              <SectionCard title={`Devis (${clientHistory.quotes?.length || 0})`} icon={FileText}>
                <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1">
                  {clientHistory.quotes?.length === 0 ? (
                    <p className="text-white/40 text-center py-4 text-sm">Aucun devis</p>
                  ) : (
                    clientHistory.quotes?.map((q) => {
                      const payCfg = q.payment_status === "fully_paid"
                        ? { color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25", dot: "bg-emerald-400", label: "Payé" }
                        : q.payment_status === "deposit_paid"
                        ? { color: "bg-amber-500/15 text-amber-400 border-amber-500/25", dot: "bg-amber-400", label: "Acompte" }
                        : { color: "bg-white/[0.06] text-white/60 border-white/[0.1]", dot: "bg-white/40", label: "En attente" };
                      return (
                        <div key={q.id} className="flex items-center gap-3 p-2.5 bg-[#1C1C1E] rounded-md border border-white/[0.04]">
                          <div className="bg-[#FF3B30]/15 text-[#FF3B30] px-2.5 py-1 rounded-md text-sm font-bold min-w-[70px] text-center">
                            <CreditCard size={11} className="inline mr-1" />{q.amount_ttc?.toFixed(0)}€
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{q.description}</p>
                            <p className="text-[11px] text-white/40">
                              {q.created_at ? new Date(q.created_at).toLocaleDateString("fr-FR") : ""}
                            </p>
                          </div>
                          <StatusBadge config={payCfg} />
                        </div>
                      );
                    })
                  )}
                </div>
              </SectionCard>

              {/* Messages */}
              <SectionCard title={`Messages (${clientHistory.messages?.length || 0})`} icon={Mail} iconColor="text-blue-400">
                <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1">
                  {clientHistory.messages?.length === 0 ? (
                    <p className="text-white/40 text-center py-4 text-sm">Aucun message</p>
                  ) : (
                    clientHistory.messages?.map((msg, idx) => (
                      <div key={msg.id || idx} className="p-3 bg-blue-500/[0.05] border border-blue-500/20 rounded-md">
                        <p className="text-sm text-white/85">{msg.message}</p>
                        <p className="text-[11px] text-white/40 mt-1">
                          {msg.created_at ? new Date(msg.created_at).toLocaleDateString("fr-FR") : ""}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </SectionCard>

              {/* Notes */}
              <SectionCard title="Notes internes" icon={StickyNote} iconColor="text-amber-400">
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Ajouter une note..."
                    className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 flex-1 text-sm"
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={!newNote.trim()}
                    className="bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/30 text-white px-3 rounded-md transition-colors"
                  >
                    <Plus size={14} />
                  </button>
                </div>
                <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1">
                  {clientHistory.notes?.length === 0 ? (
                    <p className="text-white/40 text-center py-4 text-sm">Aucune note</p>
                  ) : (
                    clientHistory.notes?.map((note) => (
                      <div key={note.id} className="flex items-start gap-3 p-3 bg-amber-500/[0.05] border border-amber-500/20 rounded-md">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white/90">{note.note}</p>
                          <p className="text-[11px] text-white/40 mt-1">
                            {new Date(note.created_at).toLocaleDateString("fr-FR")} à{" "}
                            {new Date(note.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                        <button onClick={() => handleDeleteNote(note.id)} className="text-red-400 hover:text-red-300 transition-colors">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </SectionCard>
            </div>
          )}
        </div>
      </div>

      {/* Doublons modal */}
      <DuplicatesModal
        open={duplicatesOpen}
        onClose={() => setDuplicatesOpen(false)}
        onMerged={handleFetchClientsRefresh}
        focusPhone={focusPhone}
      />
    </div>
  );
};

export default ClientsTab;
